import React, {useRef} from 'react';
import {Col, Container, Row} from 'react-bootstrap';
import PageTitle from '../../../../uiToolkit/PageTitle';
import SearchInput from '../../../../uiToolkit/Inputs/SearchInput';
import Button from '../../../../uiToolkit/Buttons/Button';
import LoadingSpinner from '../../../../uiToolkit/LoadingSpinner/LoadingSpinner';
import MicroreelsEmptyState from '../../../../uiToolkit/EmptyStates/MicroreelsEmptyState';
import {MicroreelGroup} from '../../../../data/models/Microreels/MicroreelGroup';
import {StoredGenerationInfo} from '../../Generating/generatingTypes';
import {microreelsListText} from '../microreelsListText';
import MicroreelGroupCard from '../components/MicroreelGroupCard';
import MicroreelsGeneratingCard from '../components/MicroreelsGeneratingCard';
import styles from './MicroreelsListUI.module.css';

interface Props {
  canCreate?: boolean;
  groups: MicroreelGroup[];
  onOpenGroup: (group: MicroreelGroup) => void;
  onCourseEditor: (group: MicroreelGroup) => void;
  onEditInfo: (group: MicroreelGroup) => void;
  onDelete: (group: MicroreelGroup) => void;
  onLearnerProgress: (group: MicroreelGroup) => void;
  onMoveToProject: (group: MicroreelGroup) => void;
  onCreate: () => void;
  isLoading: boolean;
  pendingGenerations: StoredGenerationInfo[];
  onOpenGenerating: (info: StoredGenerationInfo) => void;
  onCancelGeneration: () => void;
  loadNextPage: () => Promise<any>;
  search: string;
  setSearch: (search: string) => void;
}

const MicroreelsListUI = React.memo(
  ({
    groups,
    canCreate,
    onOpenGroup,
    onCourseEditor,
    onEditInfo,
    onDelete,
    onLearnerProgress,
    onMoveToProject,
    onCreate,
    isLoading,
    pendingGenerations,
    onOpenGenerating,
    onCancelGeneration,
    loadNextPage,
    search,
    setSearch,
  }: Props) => {
    const observer = useRef<any>();

    const overscrollLock = useRef(false);

    const lastPostElementRef = (node: any) => {
      if (isLoading || overscrollLock.current) return;
      if (observer.current) observer.current.disconnect();

      observer.current = new IntersectionObserver(entries => {
        if (entries[0].isIntersecting) {
          overscrollLock.current = true;
          loadNextPage().then(() => {
            overscrollLock.current = false;
          });
        }
      });

      if (node) observer.current.observe(node);
    };

    return (
      <div className={styles.container}>
        <div className={styles.header}>
          <PageTitle title={microreelsListText.title} />
          <div className={styles.filters}>
            <SearchInput value={search} onChange={setSearch} />
            {canCreate && (
              <Button
                title={microreelsListText.create}
                onClick={onCreate}
                uiType="action"
                icon="icon-plus"
              />
            )}
          </div>
        </div>
        <div>
          <Container fluid className={styles.list}>
            <Row>
              {pendingGenerations.map(info => (
                <Col xl={3} lg={4} className={styles.col} key={info.id}>
                  <MicroreelsGeneratingCard
                    title={info.title}
                    onOpen={() => onOpenGenerating(info)}
                    onCancel={onCancelGeneration}
                  />
                </Col>
              ))}
              {groups.length ? (
                groups.map((group, index) => (
                  <Col
                    xl={3}
                    lg={4}
                    key={group.id}
                    className={styles.col}
                    ref={
                      groups.length === index + 1 ? lastPostElementRef : null
                    }>
                    <MicroreelGroupCard
                      group={group}
                      onOpen={onOpenGroup}
                      onCourseEditor={onCourseEditor}
                      onEditInfo={onEditInfo}
                      onDelete={onDelete}
                      onLearnerProgress={onLearnerProgress}
                      onMoveToProject={onMoveToProject}
                    />
                  </Col>
                ))
              ) : isLoading ? (
                <Col xl={12} lg={12}>
                  <div className={styles.loading}>
                    <LoadingSpinner size={48} />
                  </div>
                </Col>
              ) : !pendingGenerations.length ? (
                <Col xl={12} lg={12}>
                  <MicroreelsEmptyState
                    title={microreelsListText.emptyTitle}
                    subtitle={microreelsListText.emptySubtitle}
                  />
                </Col>
              ) : null}
            </Row>
          </Container>
        </div>
      </div>
    );
  },
);

export default MicroreelsListUI;
